import { AxiosRequestConfig, CancelToken } from 'axios';
import { merge } from 'lodash';
import { pipe } from 'lodash/fp';

import api from '@portal/common/api/index';
import request from '@portal/common/api/request';
import {
    AiChatFeedbackListResponse,
    AiChatListResponse,
    AiChatSummary,
    AiChatsResponse,
    AiFeedbackResponse,
    AnnouncementResource,
    CountryLocationSdi,
    DataCollectionResource,
    DataGranularity,
    DataProject,
    DataRequest,
    DataResponse,
    Dataset,
    Entity,
    MethodologyResource,
    Organization,
    PaginatedRequest,
    PaginatedResponse,
    ResourceList,
    Round,
    SidebarLinkResource,
    WelcomeMessageResource,
} from '@portal/common/types';
import {
    deserializeRichText,
    serializeRichText,
    serializeToUtcDate,
} from '@portal/common/utility/api-helpers';

import config from './config';
import { EventType, VisualizationType } from './store/insights/models';
import { requestPdfExportsByDeliveryMethod } from './utility/insights/pdf-exports-delivery';

request.setApiUrl(config.apiUrl);

const UPLOAD_TIMEOUT = 24 * 3600 * 1000;
const DOWNLOAD_TIMEOUT = 3600 * 1000;

const ORGANIZATION_FIELDS = [
    'website_url',
    'address',
    'phone_number',
    'email_domains',
    'shared_links_num',
    'shared_files_num',
    'members_num',
    'self_sign_up_mode',
    'self_sign_up_token',
    'has_programmatic_access',
    'has_welcome_message_override',
    'licensed_until',
    'license_expiration_warning',
    'has_shared_surveys',
    'single_year_age_data_query_is_allowed',
    'health_threat_metrics_alert_is_enabled',
    'pharma_products_search_is_enabled',
    'pharma_products_search_is_company_restricted',
    'ai_chat_is_enabled',
    'forecast_data_calculation_is_allowed',
    'forecast_data_calculation_years',
    'available_export_options',
    'available_chart_types',
].join(',');

export default merge(api, {
    session: {
        signIn: (email, password) => request.post('admin-sessions', { email, password }),
        createImpersonate: (account_id) =>
            request.post('organization-member-sessions', { account_id }),
    },

    admins: {
        inviteAdmin: (email) => request.post(`admins`, { email }),
        updateAdmin: (id, { avatar_url, first_name, last_name, email, title, is_public }) =>
            request.put(`admins/${id}`, {
                avatar_url,
                first_name,
                last_name,
                email,
                title,
                is_public,
            }),
        getAdmin: (id) =>
            request.get(`admins/${id}`, {
                fields: '',
            }),
        getAdmins: ({ search, page, per_page }) =>
            request.get('admins', {
                search,
                page,
                per_page,
                fields: '',
            }),
        resetPassword: (email) => request.post('admin-passwords/' + email),
    },

    account: {
        getAccount: (id) => request.get('admins/' + id.toString()),
        updateAccount: (id, data) => request.put(`admins/${id}`, data),
        setRole: (id, role) => request.put(`admins/${id}/role`, { role }),
        setPassword: (id, old_password, password) =>
            request.put(`admins/${id}`, { password, old_password }),
    },

    organization: {
        addOrganization: (data) => request.post('organizations', data),
        updateOrganization: (id, data) =>
            request
                .put(
                    `organizations/${id}`,
                    pipe(serializeRichText('license_expiration_warning'))(data)
                )
                .then(pipe(deserializeRichText('license_expiration_warning'))),
        getOrganization: (id: number): Promise<Organization> =>
            request
                .get('organizations/' + id, {
                    fields: ORGANIZATION_FIELDS,
                })
                .then(pipe(deserializeRichText('license_expiration_warning'))),
        getOrganizations: ({ search, page, per_page, order_by, order_direction }) =>
            request
                .get('organizations', {
                    search,
                    page,
                    per_page,
                    order_by,
                    order_direction,
                    fields: ORGANIZATION_FIELDS,
                })
                .then((data) => ({
                    ...data,
                    results: data.results.map((item) =>
                        pipe(deserializeRichText('license_expiration_warning'))(item)
                    ),
                })),
        addOrganizationMember: (organizationId, data) =>
            request.post(`organizations/${organizationId}/members`, data),
        bulkCreateOrganizationMembers: (organizationId, members) =>
            request.patch(
                `organizations/${organizationId}/members`,
                members.map((member) => ({
                    op: 'add',
                    path: '/',
                    value: member,
                })),
                { timeout: 5 * 60 * 1_000 }
            ),
        setOrganizationMember: (organizationId, member_id, data) =>
            request.put(`organizations/${organizationId}/members/${member_id}`, data),
        deleteOrganizationMember: (organizationId, member_id) =>
            request.delete(`organizations/${organizationId}/members/${member_id}`),
        getOrganizationMembers: ({
            organizationId,
            search,
            page,
            per_page,
            status,
            order_by,
            order_direction,
        }) =>
            request.get(`organizations/${organizationId}/members`, {
                search,
                page,
                status,
                per_page,
                order_by,
                order_direction,
                fields: 'first_name,last_name,last_access,email,avatar_url,status',
            }),
        addOrganizationLink: (organizationId, link_id) =>
            request.post(`organizations/${organizationId}/link-resources/${link_id}`, {
                fields: 'organizations',
            }),
        deleteOrganizationLink: (organizationId, link_id) =>
            request.delete(`organizations/${organizationId}/link-resources/${link_id}`, {
                fields: 'organizations',
            }),
        getOrganizationLinks: ({ organizationId, search, page, per_page }) =>
            request.get(`organizations/${organizationId}/link-resources`, {
                search,
                page,
                per_page,
                fields: 'category,created,updated,sort_order,keywords',
            }),
        addOrganizationFile: (organizationId, link_id) =>
            request.post(`organizations/${organizationId}/file-resources/${link_id}`, {}),
        deleteOrganizationFile: (organizationId, link_id) =>
            request.delete(`organizations/${organizationId}/file-resources/${link_id}`),
        getOrganizationFiles: ({ organizationId, search, page, per_page }) =>
            request.get(`organizations/${organizationId}/file-resources`, {
                search,
                page,
                per_page,
                fields: 'category,created,updated,sort_order,keywords',
            }),

        getOrganizationDataCollections: ({
            organizationId,
            search,
            page,
            per_page,
            order_by,
            order_direction,
        }) =>
            request.get(`organizations/${organizationId}/data-collection-resources`, {
                search,
                page,
                per_page,
                order_by,
                order_direction,
                fields: 'category,creator{first_name,last_name,email},data_percentage,created,updated,sort_order,keywords,organizations',
            }),

        shareDataCollectionWithOrganization: (
            organizationId,
            dataCollectionId
        ): Promise<DataCollectionResource> =>
            request.post(
                `organizations/${organizationId}/data-collection-resources/${dataCollectionId}`,
                {}
            ),
        unshareDataCollectionWithOrganization: (
            organizationId,
            dataCollectionId
        ): Promise<DataCollectionResource> =>
            request.delete(
                `organizations/${organizationId}/data-collection-resources/${dataCollectionId}`
            ),

        getOrganizationFileDownloadUrl: (organizationId: number, resourceId: number): string =>
            `/organizations/${organizationId}/file-resources/${resourceId}/download`,
        regenerateSelfSignUpUrl: ({ organizationId }) =>
            request.post(`organizations/${organizationId}/self_sign_up_token`, {}),

        // best-practice
        getWelcomeMessageOverride: (params: {
            organizationId: number;
        }): Promise<WelcomeMessageResource> =>
            request
                .get(`/organizations/${params.organizationId}/welcome-message-override`, {
                    fields: 'creator{first_name,last_name,email},created,updated',
                })
                .then(pipe(deserializeRichText('text'), deserializeRichText('title'))),
        createWelcomeMessageOverride: (
            params: { organizationId: number },
            resource: Partial<Pick<WelcomeMessageResource, 'title' | 'text' | 'is_enabled'>>
        ): Promise<WelcomeMessageResource> =>
            request
                .post(
                    `/organizations/${params.organizationId}/welcome-message-override`,
                    pipe(serializeRichText('text'), serializeRichText('title'))(resource),
                    { timeout: 90_000 }
                )
                .then(pipe(deserializeRichText('text'), deserializeRichText('title'))),
        updateWelcomeMessageOverride: (
            params: { organizationId: number },
            resource: Partial<Pick<WelcomeMessageResource, 'title' | 'text' | 'is_enabled'>>
        ): Promise<WelcomeMessageResource> =>
            request
                .put(
                    `/organizations/${params.organizationId}/welcome-message-override`,
                    pipe(serializeRichText('text'), serializeRichText('title'))(resource),
                    { timeout: 30_000 }
                )
                .then(pipe(deserializeRichText('text'), deserializeRichText('title'))),
    },

    content: {
        // Categories
        addCategory: ({ name, description }) =>
            request.post('resource-categories', {
                name,
                description,
            }),
        setCategory: (id, { name, description }) =>
            request.put(`resource-categories/${id}`, {
                name,
                description,
                fields: 'description,updated,resources_num,creator{first_name,last_name,email},created',
            }),
        deleteCategory: (id) => request.delete(`resource-categories/${id}`),
        getCategories: ({ search, page, per_page }) =>
            request.get('resource-categories', {
                search,
                page,
                per_page,
                fields: 'description,updated,resources_num,creator{first_name,last_name,email},created',
            }),
        // Links
        addLink: ({ name, url, resource_category_id, keywords }) =>
            request.post('link-resources', {
                resource_category_id,
                name,
                url,
                keywords,
            }),
        setLink: (id, { name, url, resource_category_id, keywords }) =>
            request.put(`link-resources/${id}`, {
                resource_category_id,
                name,
                url,
                keywords,
                fields: 'category,organizations,creator{first_name,last_name,email},created,updated,sort_order,keywords',
            }),
        deleteLink: (id) => request.delete(`link-resources/${id}`),
        getLinks: ({ search, page, per_page }) =>
            request.get('link-resources', {
                search,
                page,
                per_page,
                fields: 'category,organizations,creator{first_name,last_name,email},created,updated,sort_order,keywords',
            }),

        // Files
        addFile: ({ name, url, resource_category_id, keywords }) =>
            request.post(
                'file-resources',
                {
                    resource_category_id,
                    name,
                    url,
                    keywords,
                },
                { timeout: 90_000 }
            ),
        setFile: (id, { name, url, resource_category_id, keywords }) =>
            request.put(
                `file-resources/${id}`,
                {
                    resource_category_id,
                    name,
                    url,
                    keywords,
                    fields: 'category,organizations,creator{first_name,last_name,email},created,updated,sort_order,keywords',
                },
                { timeout: 30_000 }
            ),
        deleteFile: (id) => request.delete(`file-resources/${id}`),
        getFileDownloadUrl: (resourceId: number): string =>
            `/file-resources/${resourceId}/download`,
        getFiles: ({ search, page, per_page }) =>
            request.get('file-resources', {
                search,
                page,
                per_page,
                fields: 'category,organizations,creator{first_name,last_name,email},created,updated,sort_order,keywords',
            }),
        // Welcome messages
        getWelcomeMessages: (
            paginationParams: PaginatedRequest<WelcomeMessageResource>
        ): Promise<PaginatedResponse<WelcomeMessageResource>> =>
            request
                .get('welcome-messages', {
                    ...paginationParams,
                    fields: 'organizations,creator{first_name,last_name,email},created,updated,sort_order,keywords',
                })
                .then((response: PaginatedResponse<WelcomeMessageResource>) => ({
                    ...response,
                    results: response.results.map(
                        pipe(deserializeRichText('text'), deserializeRichText('title'))
                    ),
                })),
        createWelcomeMessage: (
            resource: Pick<WelcomeMessageResource, 'title' | 'text' | 'name' | 'is_enabled'>
        ): Promise<WelcomeMessageResource> =>
            request
                .post('welcome-messages', {
                    ...resource,
                    ...pipe(serializeRichText('text'), serializeRichText('title'))(resource),
                })
                .then(pipe(deserializeRichText('text'), deserializeRichText('title'))),
        updateWelcomeMessage: (
            id: number,
            resource: Pick<WelcomeMessageResource, 'title' | 'text' | 'name' | 'is_enabled'>
        ): Promise<WelcomeMessageResource> =>
            request
                .put(`welcome-messages/${id}`, {
                    ...resource,
                    ...pipe(serializeRichText('text'), serializeRichText('title'))(resource),
                })
                .then(pipe(deserializeRichText('text'), deserializeRichText('title'))),
        deleteWelcomeMessage: (id: number): Promise<WelcomeMessageResource> =>
            request.delete(`welcome-messages/${id}`),
        // Methodology
        getMethodologies: (
            paginationParams: PaginatedRequest<MethodologyResource>
        ): Promise<PaginatedResponse<MethodologyResource>> =>
            request.get('methodologies', {
                ...paginationParams,
                fields: 'creator{first_name,last_name,email},created,updated,sort_order',
                order_by: 'sort_order',
            }),
        createMethodology: (resource: Partial<MethodologyResource>): Promise<MethodologyResource> =>
            request.post('methodologies', resource),
        updateMethodology: (
            id: number,
            resource: Partial<MethodologyResource>
        ): Promise<MethodologyResource> => request.put(`methodologies/${id}`, resource),
        deleteMethodology: (id: number): Promise<MethodologyResource> =>
            request.delete(`methodologies/${id}`),
        // Announcements
        getAnnouncements: (
            paginationParams: PaginatedRequest<AnnouncementResource>
        ): Promise<PaginatedResponse<AnnouncementResource>> =>
            request.get('announcements', {
                ...paginationParams,
                fields: 'creator{first_name,last_name,email},created,updated,sort_order,keywords,organizations,organization_ids,is_live',
            }),
        createAnnouncement: (
            resource: Partial<AnnouncementResource>
        ): Promise<AnnouncementResource> =>
            request.post(
                'announcements',
                {
                    ...resource,
                    display_after: serializeToUtcDate(resource.display_after),
                },
                { timeout: 90_000 }
            ),
        updateAnnouncement: (
            id: number,
            resource: Partial<AnnouncementResource>
        ): Promise<AnnouncementResource> =>
            request.put(
                `announcements/${id}`,
                {
                    ...resource,
                    display_after: serializeToUtcDate(resource.display_after),
                },
                { timeout: 30_000 }
            ),
        deleteAnnouncement: (id: number): Promise<AnnouncementResource> =>
            request.delete(`announcements/${id}`),
        // Sidebar Links
        getSidebarLinks: (
            paginationParams: PaginatedRequest<SidebarLinkResource>
        ): Promise<PaginatedResponse<SidebarLinkResource>> =>
            request.get('sidebar-links', {
                ...paginationParams,
                fields: 'creator{first_name,last_name,email},created,updated,sort_order,keywords,organizations,organization_ids',
            }),
        createSidebarLink: (resource: Partial<SidebarLinkResource>): Promise<SidebarLinkResource> =>
            request.post('sidebar-links', resource, { timeout: 90_000 }),
        updateSidebarLink: (
            id: number,
            resource: Partial<SidebarLinkResource>
        ): Promise<SidebarLinkResource> =>
            request.put(`sidebar-links/${id}`, resource, { timeout: 30_000 }),
        deleteSidebarLink: (id: number): Promise<SidebarLinkResource> =>
            request.delete(`sidebar-links/${id}`),
    },

    organizationPublicResourceList: {
        getPublicResourceLists: ({
            organizationId,
            ...params
        }: PaginatedRequest<ResourceList> & {
            organizationId: number;
        }): Promise<PaginatedResponse<ResourceList>> =>
            request.get(`organizations/${organizationId}/public-resource-lists`, params),

        makeResourceListDefault: (organizationId: number, listId: number): Promise<ResourceList> =>
            request.post(
                `/organizations/${organizationId}/public-resource-lists/${listId}/is_default`,
                { fields: 'resource_ids' }
            ),

        makeResourceListNotDefault: (
            organizationId: number,
            listId: number
        ): Promise<ResourceList> =>
            request.delete(
                `/organizations/${organizationId}/public-resource-lists/${listId}/is_default`,
                { fields: 'resource_ids' }
            ),
    },

    actions: {
        get: (search, page, per_page) =>
            request.get('admin-actions', {
                search,
                page,
                per_page,
                fields: 'description,updated,resources_num',
            }),
    },

    datasets: {
        get: (): Promise<PaginatedResponse<Dataset>> =>
            request.get('datasets', {
                page: 1,
                per_page: 100,
            }),
        getGranularity: ({
            id,
            round_id,
        }: {
            id: number;
            round_id: number;
        }): Promise<DataGranularity> =>
            request.get(`datasets/${id}/granularity`, {
                round_id,
            }),
    },

    dataProjects: {
        get: (): Promise<DataProject[]> => request.get('data-projects'),
    },

    rounds: {
        get: (): Promise<Round[]> => request.get('rounds'),
    },

    dataCollectionResources: {
        getAll: ({
            search = '',
            page = 1,
            per_page = 9999999,
            order_by = 'sort_order',
            order_direction = 'ASC',
            fields = '',
        }: PaginatedRequest<DataCollectionResource>): Promise<
            PaginatedResponse<DataCollectionResource>
        > =>
            request.get(
                'data-collection-resources',
                {
                    order_by,
                    order_direction,
                    search,
                    page,
                    per_page,
                    fields,
                },
                { timeout: 90_000 }
            ),
        get: (itemId: string): Promise<DataCollectionResource> =>
            request.get(`data-collection-resources/${itemId}`, {
                fields: 'created,updated,sort_order,keywords,default_granularity_filters_values',
            }),
        create: (item: DataCollectionResource): Promise<DataCollectionResource> =>
            request.post('data-collection-resources', item),
        update: (
            itemId: string,
            partialItem: Partial<DataCollectionResource>
        ): Promise<DataCollectionResource> =>
            request.put(`data-collection-resources/${itemId}`, partialItem),
        delete: (itemId: string): Promise<DataCollectionResource> =>
            request.delete(`data-collection-resources/${itemId}`),
    },

    // TODO: move to common
    data: {
        getCauseForecastFilters: () =>
            request.get('data/cause-forecast-filters', {}, { timeout: 100_000 }),
        getCauses: (): Promise<Entity[]> =>
            request
                .get('causes', {
                    page: 1,
                    per_page: 1000,
                })
                .then((res) => res.results),
        getSequelas: (): Promise<Entity[]> =>
            request
                .get('sequelas', {
                    page: 1,
                    per_page: 3000,
                })
                .then((res) => res.results),
        getRei: (type): Promise<Entity[]> =>
            request
                .get('rei', {
                    type,
                    page: 1,
                    per_page: 1000,
                })
                .then((res) => res.results),
        queryDataCollectionResourceData: (
            organizationId,
            resourceId,
            dataType,
            filters,
            requestConfig?: AxiosRequestConfig
        ): Promise<Record<string, DataResponse>> =>
            request
                .get(
                    `/organizations/${organizationId}/data-collection-resources/${resourceId}/${dataType}/data`,
                    filters,
                    { timeout: 100_000, ...requestConfig }
                )
                .then((data) => ({
                    [dataType]: data,
                })),
        getForecastScenarios: () => request.get('data/forecast-scenarios'),
        // { location_id, age_group_id, gender_id, cause_id, measure_id, metric_id, forecast_scenario_id (optional), year (optional) }
        getCauseForecasts: (dataRequest: DataRequest): Promise<DataResponse> =>
            request.get(
                'data/cause-forecasts',
                {
                    location_id: config.globalMapCountryLocationsIds,
                    ...dataRequest,
                },
                { timeout: 30_000 }
            ),
        getCountryLocationsSdi: (): Promise<CountryLocationSdi[]> =>
            request.get('country-locations-sdi-quintiles', {}, { timeout: 30_000 }),
    },

    ai: {
        getChatById: (chatId: string, requestConfig?: AxiosRequestConfig): Promise<AiChatSummary> =>
            request.get(`ai/chats/${chatId}`, {}, requestConfig),
    },

    analyticsReports: {
        [EventType.visit]: (params): Promise<DataResponse> => request.get('stats/visits', params),
        [EventType.pageView]: (params): Promise<DataResponse> =>
            request.get('stats/page-views', params),
        [EventType.fileDownload]: (params): Promise<DataResponse> =>
            request.get('stats/file-downloads', params),
        [EventType.dataQuery]: (params): Promise<DataResponse> =>
            request.get('stats/data-queries', params),
        [EventType.dataExport]: (params): Promise<DataResponse> =>
            request.get('stats/data-exports', params),
        [EventType.pdfExport]: (params): Promise<DataResponse> =>
            requestPdfExportsByDeliveryMethod(params, (resource, requestParams) =>
                request.get(resource, requestParams)
            ),
        [EventType.userFeedback]: (params): Promise<DataResponse> =>
            request.get('stats/tickets', params),
        [EventType.aiChat]: (params): Promise<AiChatListResponse> =>
            request.get('ai/chats', params),
        [EventType.aiFeedback]: (params): Promise<AiChatFeedbackListResponse> =>
            request.get('ai/feedbacks', params),
        [EventType.conditionSearch]: (params): Promise<DataResponse> =>
            request.get('stats/condition-searches', params),
        [EventType.companyUsage]: {
            [VisualizationType.TopLocations]: (params): Promise<DataResponse> =>
                request.get('stats/data-queries', params),
            [VisualizationType.QueryVolume]: (params): Promise<DataResponse> =>
                request.get('stats/data-queries', params),
            [VisualizationType.TotalQueries]: (params): Promise<DataResponse> =>
                request.get('stats/data-queries', params),
            [VisualizationType.TotalVisits]: (params): Promise<DataResponse> =>
                request.get('stats/visits', params),
            [VisualizationType.EngagementTrends]: (params): Promise<DataResponse> =>
                request.get('stats/avg-data-queries-per-session', params),
            [VisualizationType.LicenseInformation]: ({
                organization_id,
                ...params
            }): Promise<DataResponse> =>
                request.get(
                    `organizations/${organization_id}/data-availability-percentage`,
                    params
                ),
            [VisualizationType.QueriesByDataType]: (params): Promise<DataResponse> =>
                request.get('stats/data-queries', params),
        },
    },

    // TODO: move to common
    downloadFile: ({ url, onProgress, cancelController }) =>
        request.get(url, null, {
            responseType: 'blob',
            onDownloadProgress: onProgress,
            timeout: DOWNLOAD_TIMEOUT,
            transformResponse: [
                (payload, headers) => {
                    if (headers['content-type'] === 'application/json') {
                        throw Error(
                            'Server download error. Please try again later or contact IHME support.'
                        );
                    }

                    if (headers['content-type'] === 'application/octet-stream') {
                        const filenameRegExp =
                            /filename[^;=\n]*=(?:(\\?['"])(.*?)\1|(?:[^\s]+'.*?')?([^;\n]*))/;

                        const [_0, _1, quotedMatch, unquotedMatch] =
                            filenameRegExp.exec(headers['content-disposition']) || [];

                        if (!quotedMatch && !unquotedMatch) {
                            throw Error('Error parsing Content-Disposition header from the server');
                        }

                        const name = quotedMatch || unquotedMatch;
                        const data = new Blob([payload]);

                        return {
                            data: {
                                name,
                                data,
                            },
                        };
                    }

                    return payload;
                },
            ],
            cancelToken: new CancelToken((cancelCallback) => {
                if (cancelController) {
                    cancelController.cancel = cancelCallback;
                }
            }),
        }),

    // TODO: move to common
    uploadFile: ({ file, onProgress, cancelController }) => {
        const formData = new FormData();
        formData.append('file', file);

        return request.post('uploads', formData, {
            onUploadProgress: onProgress,
            transformRequest: [
                (payload, headers) => {
                    headers.Accept = 'text/plain';
                    return payload;
                },
            ],
            timeout: UPLOAD_TIMEOUT,
            responseType: 'text',
            transformResponse: [
                (payload) => {
                    const data = payload.trim().split('\n').map(JSON.parse);
                    return { data };
                },
            ],
            cancelToken: new CancelToken((cancelCallback) => {
                if (cancelController) {
                    cancelController.cancel = cancelCallback;
                }
            }),
        });
    },

    getUploadedFilePreviewUrl: (uploadedFileId: number): Promise<string> =>
        request.get(`/uploads/${uploadedFileId}/view`),

    emailConfirmation: {
        resend: (email) => request.post(`email-confirmation`, { email }),
    },

    members: {
        resendInvite: (email) => request.post(`organization-member-invites/${email}`, {}),
    },
});
